// PayPal client config + Cloudflare Pages Function calls (static-friendly).
import { useEffect, useState } from "react";

// Base for the PayPal functions; prefer the configured backend, else same-origin.
const API_BASE = process.env.REACT_APP_PAYPAL_API_BASE || process.env.REACT_APP_BACKEND_URL || "";

// Optional build-time override. Normally leave this UNSET: the Client ID is read at
// runtime from /api/paypal/config (= PAYPAL_CLIENT_ID in Cloudflare), which guarantees
// the buttons use the same PayPal app + sandbox/live mode as the server. A mismatch
// between the two is the #1 cause of "PayPal encountered an error".
const BUILD_CLIENT_ID = (process.env.REACT_APP_PAYPAL_CLIENT_ID || "").trim();

let configPromise = null;
function loadPaypalConfig() {
  if (!configPromise) {
    configPromise = fetch(`${API_BASE}/api/paypal/config`)
      .then((r) => (r.ok ? r.json() : {}))
      .catch(() => ({}))
      .then((d) => {
        const clientId = (d.clientId || BUILD_CLIENT_ID || "").trim();
        if (d.clientId && BUILD_CLIENT_ID && d.clientId !== BUILD_CLIENT_ID) {
          console.warn(
            "[PayPal] REACT_APP_PAYPAL_CLIENT_ID differs from the server's PAYPAL_CLIENT_ID — using the server's."
          );
        }
        return {
          clientId,
          env: d.env || "sandbox",
          // Stable options object so the SDK provider doesn't re-init on every render.
          options: clientId ? { clientId, currency: "USD", intent: "capture" } : null,
        };
      });
  }
  return configPromise;
}

// React hook: { loading, clientId, env, options }
export function usePaypalConfig() {
  const [cfg, setCfg] = useState({ loading: true, clientId: "", env: "sandbox", options: null });
  useEffect(() => {
    let alive = true;
    loadPaypalConfig().then((c) => alive && setCfg({ loading: false, ...c }));
    return () => {
      alive = false;
    };
  }, []);
  return cfg;
}

// Reads a JSON error body from our functions and turns it into a helpful message.
async function errorFrom(r, fallback) {
  const d = await r.json().catch(() => ({}));
  const msg = d.error || d.detail || fallback;
  const extra = [d.paypal_issue || d.paypal_error, d.debug_id && `debug_id ${d.debug_id}`].filter(Boolean).join(", ");
  console.error(`[PayPal] ${fallback}:`, r.status, d);
  return new Error(extra ? `${msg} (${extra})` : msg);
}

export async function createPaypalOrder(data) {
  const r = await fetch(`${API_BASE}/api/paypal/create-order`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(data),
  });
  if (!r.ok) throw await errorFrom(r, "create-order failed");
  const d = await r.json();
  if (!d.id) throw new Error(d.error || "no order id returned");
  return d;
}

export async function capturePaypalOrder(paypalOrderId, data) {
  const r = await fetch(`${API_BASE}/api/paypal/capture-order/${paypalOrderId}`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(data || {}),
  });
  if (!r.ok) throw await errorFrom(r, "capture failed");
  return r.json();
}
